import * as Cesium from "cesium";
import { includesNormalized, showToast } from "../../../utilies/Utility";
import Type from "../../../utilies/Types";
import { load3DTiles } from "../Load3DTiles";
import { loadGeojsonImageType } from "../LoadCamera";
import { loadGeojsonTypeAsync } from "../LoadGeojson";
import { serviceGetLayerFromPostgreSQL } from "../../../api/services/layer";
import { loadAerialImageryTypeAsync } from "../LoadImagery";

const NON_GEOJSON_LAYER_TYPES = new Set(["3d_tiles", "camera", "aerial_imagery"]);

const cloneGeojson = (value) => {
  try {
    return JSON.parse(JSON.stringify(value ?? null));
  } catch {
    return value ?? null;
  }
};

const normalizeLayerCacheKey = (raw) => {
  return String(raw ?? "")
    .toLowerCase()
    .replace(/[\s\-_]/g, "");
};

const isNonGeoJsonLayer = (layer) => {
  return NON_GEOJSON_LAYER_TYPES.has(String(layer?.layer_type || "").toLowerCase());
};

const toFeatureCollection = (features = []) => ({
  type: "FeatureCollection",
  features,
});

const normalizeLongitudeRad = (value) => {
  let lng = value;

  while (lng < -Math.PI) lng += Math.PI * 2;
  while (lng > Math.PI) lng -= Math.PI * 2;

  return lng;
};

const isLongitudeInside = (lng, west, east) => {
  if (west <= east) return lng >= west && lng <= east;
  return lng >= west || lng <= east;
};

const rectangleToDegrees = (bboxRectangle) => ({
  west: Cesium.Math.toDegrees(bboxRectangle.west),
  east: Cesium.Math.toDegrees(bboxRectangle.east),
  south: Cesium.Math.toDegrees(bboxRectangle.south),
  north: Cesium.Math.toDegrees(bboxRectangle.north),
});

const isRouteLayerName = (layerName) => {
  return normalizeLayerCacheKey(layerName || "").startsWith("route");
};

const isCoordinateInsideBBox = (coord, bboxRectangle) => {
  if (!Array.isArray(coord) || coord.length < 2 || !bboxRectangle) return false;

  const lngDeg = Number(coord[0]);
  const latDeg = Number(coord[1]);

  if (!Number.isFinite(lngDeg) || !Number.isFinite(latDeg)) return false;

  const lng = normalizeLongitudeRad((lngDeg * Math.PI) / 180);
  const lat = (latDeg * Math.PI) / 180;

  return isLongitudeInside(lng, bboxRectangle.west, bboxRectangle.east) && lat >= bboxRectangle.south && lat <= bboxRectangle.north;
};

const areAllCoordinatesInsideBBox = (coords, bboxRectangle) => {
  if (!Array.isArray(coords)) return false;
  if (coords.length === 0) return false;

  if (typeof coords[0] === "number") {
    return isCoordinateInsideBBox(coords, bboxRectangle);
  }

  return coords.every((item) => areAllCoordinatesInsideBBox(item, bboxRectangle));
};

const flattenCoordinates = (coords, out = []) => {
  if (!Array.isArray(coords)) return out;

  if (coords.length >= 2 && typeof coords[0] === "number" && typeof coords[1] === "number") {
    out.push(coords);
    return out;
  }

  for (const item of coords) {
    flattenCoordinates(item, out);
  }

  return out;
};

const getSegmentsFromPointArray = (points, closeLoop = false) => {
  const segments = [];
  if (!Array.isArray(points) || points.length < 2) return segments;

  for (let i = 0; i < points.length - 1; i++) {
    segments.push([points[i], points[i + 1]]);
  }

  if (closeLoop && points.length > 2) {
    segments.push([points[points.length - 1], points[0]]);
  }

  return segments;
};

const getGeometrySegments = (geometry) => {
  if (!geometry) return [];

  if (geometry.type === "LineString") {
    return getSegmentsFromPointArray(geometry.coordinates, false);
  }

  if (geometry.type === "MultiLineString") {
    return geometry.coordinates.flatMap((line) => getSegmentsFromPointArray(line, false));
  }

  if (geometry.type === "Polygon") {
    return geometry.coordinates.flatMap((ring) => getSegmentsFromPointArray(ring, true));
  }

  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates.flatMap((polygon) => polygon.flatMap((ring) => getSegmentsFromPointArray(ring, true)));
  }

  if (geometry.type === "GeometryCollection") {
    return Array.isArray(geometry.geometries) ? geometry.geometries.flatMap((item) => getGeometrySegments(item)) : [];
  }

  return [];
};

const getBBoxCorners = (bboxDeg) => {
  return [
    [bboxDeg.west, bboxDeg.south],
    [bboxDeg.east, bboxDeg.south],
    [bboxDeg.east, bboxDeg.north],
    [bboxDeg.west, bboxDeg.north],
  ];
};

const isPointInsideBBoxDeg = (point, bboxDeg) => {
  if (!Array.isArray(point) || point.length < 2) return false;

  const lon = Number(point[0]);
  const lat = Number(point[1]);

  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return false;

  const insideLon = bboxDeg.west <= bboxDeg.east ? lon >= bboxDeg.west && lon <= bboxDeg.east : lon >= bboxDeg.west || lon <= bboxDeg.east;

  return insideLon && lat >= bboxDeg.south && lat <= bboxDeg.north;
};

const cross2d = (a, b, c) => {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
};

const isPointOnSegment = (p, a, b) => {
  const eps = 1e-9;
  if (Math.abs(cross2d(a, b, p)) > eps) return false;

  return (
    p[0] >= Math.min(a[0], b[0]) - eps &&
    p[0] <= Math.max(a[0], b[0]) + eps &&
    p[1] >= Math.min(a[1], b[1]) - eps &&
    p[1] <= Math.max(a[1], b[1]) + eps
  );
};

const segmentsIntersect = (a1, a2, b1, b2) => {
  const d1 = cross2d(a1, a2, b1);
  const d2 = cross2d(a1, a2, b2);
  const d3 = cross2d(b1, b2, a1);
  const d4 = cross2d(b1, b2, a2);
  const eps = 1e-9;

  if (((d1 > eps && d2 < -eps) || (d1 < -eps && d2 > eps)) && ((d3 > eps && d4 < -eps) || (d3 < -eps && d4 > eps))) {
    return true;
  }

  if (Math.abs(d1) <= eps && isPointOnSegment(b1, a1, a2)) return true;
  if (Math.abs(d2) <= eps && isPointOnSegment(b2, a1, a2)) return true;
  if (Math.abs(d3) <= eps && isPointOnSegment(a1, b1, b2)) return true;
  if (Math.abs(d4) <= eps && isPointOnSegment(a2, b1, b2)) return true;

  return false;
};

const segmentIntersectsBBox = (segment, bboxDeg) => {
  const [start, end] = segment;
  if (isPointInsideBBoxDeg(start, bboxDeg) || isPointInsideBBoxDeg(end, bboxDeg)) return true;

  const corners = getBBoxCorners(bboxDeg);
  const edges = [
    [corners[0], corners[1]],
    [corners[1], corners[2]],
    [corners[2], corners[3]],
    [corners[3], corners[0]],
  ];

  return edges.some(([a, b]) => segmentsIntersect(start, end, a, b));
};

const isPointInRing = (point, ring) => {
  if (!Array.isArray(ring) || ring.length < 3) return false;

  let inside = false;

  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = Number(ring[i][0]);
    const yi = Number(ring[i][1]);
    const xj = Number(ring[j][0]);
    const yj = Number(ring[j][1]);

    if (![xi, yi, xj, yj].every(Number.isFinite)) continue;

    const intersects = yi > point[1] !== yj > point[1] && point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi || Number.EPSILON) + xi;

    if (intersects) inside = !inside;
  }

  return inside;
};

const isPointInPolygonCoordinates = (point, polygonCoordinates) => {
  if (!Array.isArray(polygonCoordinates) || polygonCoordinates.length === 0) return false;
  if (!isPointInRing(point, polygonCoordinates[0])) return false;

  for (let i = 1; i < polygonCoordinates.length; i++) {
    if (isPointInRing(point, polygonCoordinates[i])) return false;
  }

  return true;
};

const geometryIntersectsBBox = (geometry, bboxRectangle) => {
  if (!geometry || !bboxRectangle) return false;

  if (geometry.type === "GeometryCollection") {
    return Array.isArray(geometry.geometries) && geometry.geometries.some((item) => geometryIntersectsBBox(item, bboxRectangle));
  }

  const bboxDeg = rectangleToDegrees(bboxRectangle);
  const points = flattenCoordinates(geometry.coordinates, []);

  if (points.some((point) => isPointInsideBBoxDeg(point, bboxDeg))) {
    return true;
  }

  const segments = getGeometrySegments(geometry);
  if (segments.some((segment) => segmentIntersectsBBox(segment, bboxDeg))) {
    return true;
  }

  const bboxCorners = getBBoxCorners(bboxDeg);

  if (geometry.type === "Polygon") {
    return bboxCorners.some((corner) => isPointInPolygonCoordinates(corner, geometry.coordinates));
  }

  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates.some((polygon) => bboxCorners.some((corner) => isPointInPolygonCoordinates(corner, polygon)));
  }

  return false;
};

const isGeometryFullyInsideBBox = (geometry, bboxRectangle) => {
  if (!geometry || !bboxRectangle) return false;

  if (geometry.type === "GeometryCollection") {
    return Array.isArray(geometry.geometries) && geometry.geometries.every((item) => isGeometryFullyInsideBBox(item, bboxRectangle));
  }

  return areAllCoordinatesInsideBBox(geometry.coordinates, bboxRectangle);
};

const getFeatureNumericId = (feature) => {
  const value = Number(feature?.properties?.id);
  return Number.isFinite(value) ? value : null;
};

const buildBBoxGeojsonPayload = (layer, geojson, bboxRectangle) => {
  const features = Array.isArray(geojson?.features) ? geojson.features : [];
  const visibleFeatures = [];
  const hiddenFeatures = [];
  let maxId = 0;

  const fullGeojson = cloneGeojson({
    type: geojson?.type || "FeatureCollection",
    features,
  });

  if (isRouteLayerName(layer?.layer_name)) {
    const hasIntersection = features.some((feature) => geometryIntersectsBBox(feature?.geometry, bboxRectangle));

    for (const feature of features) {
      const numericId = getFeatureNumericId(feature);
      if (Number.isFinite(numericId)) {
        maxId = Math.max(maxId, numericId);
      }
    }

    return {
      fullGeojson,
      visibleGeojson: hasIntersection ? cloneGeojson(fullGeojson) : toFeatureCollection([]),
      hiddenGeojson: toFeatureCollection([]),
      maxId,
      shouldRender: hasIntersection && features.length > 0,
    };
  }

  for (const feature of features) {
    const numericId = getFeatureNumericId(feature);
    if (Number.isFinite(numericId)) {
      maxId = Math.max(maxId, numericId);
    }

    if (isGeometryFullyInsideBBox(feature?.geometry, bboxRectangle)) {
      visibleFeatures.push(cloneGeojson(feature));
    } else {
      hiddenFeatures.push(cloneGeojson(feature));
    }
  }

  return {
    fullGeojson,
    visibleGeojson: toFeatureCollection(visibleFeatures),
    hiddenGeojson: toFeatureCollection(hiddenFeatures),
    maxId,
    shouldRender: visibleFeatures.length > 0,
  };
};

const attachHiddenGeojsonToMapObject = ({ setMapObjects, projectId, layerId, layerName, hiddenGeojson, fullGeojson, bboxMaxId }) => {
  const tryAttach = (retry = 0) => {
    let matched = false;

    setMapObjects((prev) =>
      prev.map((item) => {
        const sameProject = String(item?.project_id) === String(projectId);
        const sameLayerId = String(item?.layer_id) === String(layerId);
        const sameName = normalizeLayerCacheKey(item?.file_name || "") === normalizeLayerCacheKey(layerName || "") || String(item?.file_name || "") === String(layerName || "");

        if (!sameProject || (!sameLayerId && !sameName)) {
          return item;
        }

        matched = true;

        return {
          ...item,
          hiddenGeojson: cloneGeojson(hiddenGeojson),
          fullGeojson: cloneGeojson(fullGeojson),
          bboxMaxId,
          bboxMaxFeaturePropertiesId: bboxMaxId,
          loadOnlyInsideBbox: true,
        };
      }),
    );

    if (!matched && retry < 20) {
      setTimeout(() => tryAttach(retry + 1), 150);
    }
  };

  tryAttach(0);
};

const incrementLoadingProgress = (loadingDataForLayers) => {
  if (!loadingDataForLayers?.current) return;

  const currentValue = Number(loadingDataForLayers.current.current || 0) + 1;
  const totalValue = Number(loadingDataForLayers.current.total || 0);

  loadingDataForLayers.current = {
    current: currentValue,
    total: totalValue,
    message: loadingDataForLayers.current?.message,
    show: currentValue < totalValue,
  };
};

export const loadProjectLayers = async ({
  project,
  projectTypeLoading = "main",

  viewerRef,
  userData,
  setUserType,
  setUserCanEditing,
  loadingDataForLayers,

  setPointCloudStyle,
  setMapObjects,
  setMapObjectsAssest,

  setImageGeojson,
  setImageGeojsonSelected,
  setImageGeojsonMode,
  setImageDataSource,
  setImageGeojsonCounter,

  originalCameraControls,
  defaultGeojsonStyle,
  basicGeojsonStyle,
  defaultOsmStyle,
  applyColorChange = false,

  layerFilter = null,
  bboxRectangle = null,
  onlyNonGeoJsonLayers = false,
  skipNonGeoJsonLayers = false,
}) => {
  if (!viewerRef?.current) {
    console.warn("Viewer not ready, skip loadProjectLayers for", project?.id);
    return;
  }

  const contributors = Array.isArray(project?.contributors) ? project.contributors : [];

  if (contributors[0]?.username) {
    for (const contributor of contributors) {
      if (contributor.username == userData.user.username) {
        setUserType(contributor.role);

        if (contributor.role == Type.ROLE.ADMIN || contributor.role == Type.ROLE.ANNOTATOR || contributor.role == Type.ROLE.DIGITIZER || contributor.role == Type.ROLE.SUPER_ADMIN) {
          setUserCanEditing(true);
        }
        break;
      }
    }
  }

  const allProjectLayers = Array.isArray(project?.layers) ? project.layers : [];
  const tempLayers = allProjectLayers.filter((layer) => {
    const nonGeoJson = isNonGeoJsonLayer(layer);

    if (onlyNonGeoJsonLayers && !nonGeoJson) return false;
    if (skipNonGeoJsonLayers && nonGeoJson) return false;

    if (typeof layerFilter === "function") {
      return !!layerFilter(layer);
    }

    if (Array.isArray(layerFilter) && layerFilter.length) {
      return layerFilter.includes(layer.id) || layerFilter.includes(layer.layer_name);
    }

    return true;
  });

  const showProgressBar = tempLayers.length !== 0;
  if (showProgressBar && loadingDataForLayers?.current) {
    loadingDataForLayers.current = {
      current: 0,
      total: tempLayers.length,
      show: true,
    };
  }

  let whatLayerZoom = {
    lanelet: false,
    lanemarker: false,
    other: true,
  };

  for (const layer of tempLayers) {
    if (isNonGeoJsonLayer(layer)) continue;

    if (includesNormalized(layer.layer_name, "lanelet")) {
      whatLayerZoom = {
        lanelet: true,
        lanemarker: false,
        other: false,
      };
      break;
    } else if (includesNormalized(layer.layer_name, "lanemarker")) {
      whatLayerZoom = {
        lanelet: false,
        lanemarker: true,
        other: false,
      };
      break;
    } else {
      whatLayerZoom = {
        lanelet: false,
        lanemarker: false,
        other: true,
      };
    }
  }

  const prepareGeojsonForLoad = (layer, geojson) => {
    const safeGeojson = {
      type: geojson?.type || "FeatureCollection",
      features: Array.isArray(geojson?.features) ? geojson.features : [],
    };

    if (!bboxRectangle) {
      return {
        geojsonForRender: safeGeojson,
        fullGeojson: cloneGeojson(safeGeojson),
        hiddenGeojson: toFeatureCollection([]),
        maxId: 0,
        shouldRender: safeGeojson.features.length > 0,
      };
    }

    const payload = buildBBoxGeojsonPayload(layer, safeGeojson, bboxRectangle);

    return {
      geojsonForRender: payload.visibleGeojson,
      fullGeojson: payload.fullGeojson,
      hiddenGeojson: payload.hiddenGeojson,
      maxId: payload.maxId,
      shouldRender: payload.shouldRender,
    };
  };

  for (const layer of tempLayers) {
    if (layer.layer_type == "3d_tiles" && projectTypeLoading == "main") {
      setPointCloudStyle(layer.style);
      await load3DTiles({
        url: layer.layer_name.toLowerCase(),
        viewerRef,
        setMapObjects,
        layerId: layer.id,
        projectId: project.id,
      });
      incrementLoadingProgress(loadingDataForLayers);
      continue;
    }

    if (layer.layer_type == "camera" && projectTypeLoading == "main") {
      const tempUrl = layer.file_url;

      fetch(tempUrl)
        .then((response) => response.json())
        .then((data) => {
          loadGeojsonImageType({
            viewerRef,
            setImageGeojson,
            setImageGeojsonSelected,
            setImageGeojsonMode,
            setImageDataSource,
            setMapObjects,
            setImageGeojsonCounter,
            geojsonData: data,
            fileName: layer.layer_name,
            layerId: layer.id,
            projectId: project.id,
          });
          incrementLoadingProgress(loadingDataForLayers);
        })
        .catch((error) => {
          console.error(error);
        });

      continue;
    }

    if (layer.layer_type === "aerial_imagery" && projectTypeLoading === "main") {
      const src = layer?.style?.url;

      await loadAerialImageryTypeAsync({
        viewerRef,
        setMapObjects,
        idOrUrl: src,
        fileName: layer.layer_name || "Aerial Imagery",
        savedStyles: layer.style,
        projectId: project.id,
        serverLayerId: layer.id,
        isUrl: false,
        priority: layer?.style?.priority ?? 0,
        isDisableLoading: layer?.style?.is_disable_loading,
        maxTileErrors: layer?.style?.max_tile_errors ?? 5,
        projectTypeLoading: "main",
        onError: (e) => console.error("Aerial imagery load error:", e),
      });

      incrementLoadingProgress(loadingDataForLayers);
      continue;
    }

    const tempUrl = layer.file_url;

    if (tempUrl) {
      const mustFetchGeojson = Boolean(bboxRectangle) || !layer?.style?.is_disable_loading;

      if (!mustFetchGeojson) {
        loadGeojsonTypeAsync({
          originalCameraControls,
          geojsonData: null,
          fileName: layer.layer_name,
          viewerRef,
          setMapObjects,
          defaultGeojsonStyle,
          basicGeojsonStyle,
          projectId: project.id,
          serverLayerId: layer.id,
          isUrl: false,
          savedStyles: layer.style,
          defaultOsmStyle,
          loadingDataForLayers,
          projectTypeLoading,
          setMapObjectsAssest,
          isDisableLoading: layer?.style?.is_disable_loading,
          url: tempUrl,
          applyColorChange,
        });
        continue;
      }

      fetch(tempUrl)
        .then((response) => response.json())
        .then((data) => {
          const prepared = prepareGeojsonForLoad(layer, data);

          if (bboxRectangle && !prepared.shouldRender) {
            incrementLoadingProgress(loadingDataForLayers);
            return;
          }

          let z = false;
          if (includesNormalized(layer.layer_name, "lanelet") && whatLayerZoom.lanelet) {
            z = true;
          } else if (includesNormalized(layer.layer_name, "lanemarker") && whatLayerZoom.lanemarker) {
            z = true;
          } else if (whatLayerZoom.other) {
            z = true;
            whatLayerZoom.other = false;
          }

          loadGeojsonTypeAsync({
            originalCameraControls,
            geojsonData: prepared.geojsonForRender,
            fileName: layer.layer_name,
            viewerRef,
            setMapObjects,
            defaultGeojsonStyle,
            basicGeojsonStyle,
            projectId: project.id,
            serverLayerId: layer.id,
            isUrl: false,
            savedStyles: layer.style,
            defaultOsmStyle,
            loadingDataForLayers,
            zoomToDataSource: z,
            applyColorChange,
            projectTypeLoading,
            setMapObjectsAssest,
            isDisableLoading: false,
            fullGeojson: prepared.fullGeojson,
            hiddenGeojson: prepared.hiddenGeojson,
            bboxRectangle,
            bboxMaxId: prepared.maxId,
            loadOnlyInsideBbox: Boolean(bboxRectangle),
          });

          if (bboxRectangle) {
            attachHiddenGeojsonToMapObject({
              setMapObjects,
              projectId: project.id,
              layerId: layer.id,
              layerName: layer.layer_name,
              hiddenGeojson: prepared.hiddenGeojson,
              fullGeojson: prepared.fullGeojson,
              bboxMaxId: prepared.maxId,
            });
          }
        })
        .catch((error) => {
          console.error(error);
        });

      continue;
    }

    const formData = new FormData();
    formData.append(
      "params",
      JSON.stringify({
        table_name: layer.style.db_table,
        schema: layer.style.db_schema,
        database: layer.style.db_db || "Horizon",
        limit: 1000,
        offset: 0,
        where: layer.style.db_where || "",
        geometry_field: layer.style.db_geometry_field,
      }),
    );

    serviceGetLayerFromPostgreSQL(formData)
      .then((res) => {
        if (res.data?.status && res.data.status === "failed") {
          showToast(res.data.message, Type.NOTIFICATION.WARNING);
          return;
        }

        let geojson = res.data;
        if (!geojson) {
          showToast("There is no value for the layer " + layer.layer_name, Type.NOTIFICATION.WARNING);
          return;
        }

        if (geojson.features == null) geojson.features = [];

        const prepared = prepareGeojsonForLoad(layer, geojson);

        if (bboxRectangle && !prepared.shouldRender) {
          incrementLoadingProgress(loadingDataForLayers);
          return;
        }

        loadGeojsonTypeAsync({
          originalCameraControls,
          geojsonData: prepared.geojsonForRender,
          fileName: layer.layer_name,
          viewerRef,
          setMapObjects,
          defaultGeojsonStyle,
          basicGeojsonStyle,
          defaultOsmStyle,
          loadingDataForLayers,
          isLoadFromDatabase: true,
          sqlSchema: layer.style.db_schema,
          sqlTable: layer.style.db_table,
          sqlGeometryField: layer.style.db_geometry_field,
          sqlDB: layer.style.db_db,
          projectId: project.id,
          serverLayerId: layer.id,
          applyColorChange,
          fullGeojson: prepared.fullGeojson,
          hiddenGeojson: prepared.hiddenGeojson,
          bboxRectangle,
          bboxMaxId: prepared.maxId,
          loadOnlyInsideBbox: Boolean(bboxRectangle),
        });

        if (bboxRectangle) {
          attachHiddenGeojsonToMapObject({
            setMapObjects,
            projectId: project.id,
            layerId: layer.id,
            layerName: layer.layer_name,
            hiddenGeojson: prepared.hiddenGeojson,
            fullGeojson: prepared.fullGeojson,
            bboxMaxId: prepared.maxId,
          });
        }
      })
      .catch((err) => {
        console.error("Error", err);
      });
  }
};
